import { useState } from "react";
import { useDirectories } from "../hooks/useDirectories";
import CardDocument from "../components/CardDocument";
import {
    Box,
    Button,
    CircularProgress,
    styled,
    Typography,
} from "@mui/material";
import { DirectoryType, Document } from "../../common/redux/documentsAction";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import RenameModal from "../components/RenameModal";
import { useTranslation } from "react-i18next";
import UploadDocument from "../../profil/UploadDocument";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EmptyDataDocs from "./EmptyDataDocs";
import { useDirectory } from "../hooks/useDirectory";
import DeleteModal from "../components/DeleteModal";

const MyDocuments = () => {
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [documentToRename, setDocumentToRename] = useState<Document | null>(
        null
    );
    const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
        null
    );
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const { directories, loading: loadingDirectories } = useDirectories({
        type: DirectoryType.PERSONAL,
    });
    const {
        directory,
        loading: loadingDirectory,
        refresh: refreshDirectory,
    } = useDirectory(directories[0]?.id || null);
    const loading = loadingDirectories || loadingDirectory;
    const documentsLength = directory?.documents.length ?? 0;

    const { t } = useTranslation();

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const viewDocument = (document: Document) => {
        window.open(document.fileUri, "_blank");
    };

    const download = (doc: Document) => {
        const link = document.createElement("a");
        link.href = doc.fileUri;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={8}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box width="100%" height="100%" overflow="auto" mt={2} p={1}>
            <HeaderContainer>
                <div>
                    <Typography variant="h6">
                        {t("manageDocuments.title.section.mydocs")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {documentsLength <= 1
                            ? t("manageDocuments.subtitle.section.mydocs", {
                                  count: documentsLength,
                              })
                            : t(
                                  "manageDocuments.subtitle.section.mydocs_plural",
                                  {
                                      count: documentsLength,
                                  }
                              )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t("manageDocuments.description.section.mydocs")}
                    </Typography>
                </div>
                <Box display="flex" justifyContent="flex-start">
                    <ChooseFile
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => setOpenUploadModal(true)}
                    >
                        {t("button.upload.document")}
                    </ChooseFile>
                </Box>
            </HeaderContainer>

            {directory?.documents.length === 0 ? (
                <EmptyDataBox>
                    <EmptyDataDocs
                        title={t("manageDocuments.empty.personal.data.title")}
                        subtitle={t(
                            "manageDocuments.empty.personal.data.subtitle"
                        )}
                    />
                </EmptyDataBox>
            ) : (
                <>
                    <ContainerDocument>
                        {directory?.documents.map((document) => (
                            <Box key={document.id}>
                                <CardDocument
                                    document={document}
                                    onView={viewDocument}
                                    onRename={setDocumentToRename}
                                    onDelete={setDocumentToDelete}
                                    onDownload={download}
                                />
                            </Box>
                        ))}
                    </ContainerDocument>
                </>
            )}

            <Dialog
                open={openUploadModal}
                onClose={() => setOpenUploadModal(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenUploadModal(false)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Box sx={{ p: 2 }}>
                    <UploadDocument
                        directoryId={directory?.id || null}
                        onUploadSuccess={() => {
                            refreshDirectory();
                            setOpenUploadModal(false);
                        }}
                    />
                </Box>
            </Dialog>
            <DeleteModal
                open={documentToDelete !== null}
                document={documentToDelete}
                onClose={() => setDocumentToDelete(null)}
                onSuccess={() => {
                    refreshDirectory();
                    setDocumentToDelete(null);
                }}
            />
            <RenameModal
                open={documentToRename !== null}
                document={documentToRename}
                onClose={() => setDocumentToRename(null)}
                onSuccess={() => {
                    refreshDirectory();
                    setDocumentToRename(null);
                }}
            />
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity as any}
                handleCloseSnackbar={handleCloseSnackbar}
            />
        </Box>
    );
};

export default MyDocuments;

export const ContainerDocument = styled(Box)({
    display: "flex",
    height: "auto",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "2rem",
    flexWrap: "wrap",
    gap: "0.5rem",

    "@media(width >= 768px)": {
        justifyContent: "flex-start",
    },
});

export const HeaderContainer = styled("div")({
    display: "flex",
    flexDirection: "column",
    marginBottom: "2rem",

    "@media(width >= 768px)": {
        flexDirection: "row",
        justifyContent: "space-between",
    },
});

export const ChooseFile = styled(Button)({
    width: "100%",
    height: 50,
    backgroundColor: "#e53835",
    ":hover": {
        backgroundColor: "#e53835",
    },
});

export const EmptyDataBox = styled(Box)({
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});
